/**
 * Recognition and raising — lowered expansions back to logical blocks.
 *
 * Recognition is by TAGS: nodes carrying the same `{ traceMacro, macroId }`
 * form one group, and `macroRole` names each member. There is no pattern
 * matching. A group is accepted as an instance only when it is exactly what
 * its own raised form lowers to:
 *
 *   1. read the block's content off the group (params at the param targets,
 *      flows at the declared outputs — what a pseudo-node would carry);
 *   2. re-emit that content with the same `emitMacroInstance` lowering uses,
 *      into a scratch copy of the graph;
 *   3. compare the stored members with the re-emitted nodes, role by role —
 *      ops, configuration, literals (by type signature), value refs and flows,
 *      plus the private events/variables they address;
 *   4. and check the boundary: flow may enter only at declared inputs, nothing
 *      outside may read a member's output or address its private state.
 *
 * So raising is lossless by construction: a group that passes is rebuilt
 * byte-for-byte (modulo index numbering) by lowering its pseudo-node. A group
 * another tool rewired — a changed wire, a shared event, a stray inbound flow
 * — fails the comparison and stays plain nodes, which still execute.
 * Everything a `MacroShape` declares is checked this way; adding a feature to
 * shapes adds no recognition code.
 *
 * `legacyShapes` go through the same check, so a superseded expansion is
 * raised as the same logical block and re-lowered in the current shape.
 */

import type { FlowTarget, GraphData, GraphNode, TypeSignature } from '../../../scenedata/behaviors/behaviorgraphdata';
import { GraphOps } from '../../../scenedata/behaviors/behaviorgraphdata';
import {
    getMacro,
    MACRO_EVENT_CONFIG,
    MACRO_ID_KEY,
    MACRO_ROLE_KEY,
    MACRO_VARIABLE_CONFIG,
    TRACE_MACRO_KEY,
    type MacroDefinition,
    type MacroInstance,
    type MacroShape,
    type MacroWiredValue,
} from './registry';
import {
    declarationIndex,
    emitMacroInstance,
    pruneDeclarations,
    remapNodeRefs,
    removeTableEntries,
    type MacroInstanceContent,
} from './graphEdit';

/**
 * Metadata key `inlineSubgraphs` tags flattened subgraph-body copies with
 * (`TRACE_SUBGRAPH_KEY` in subgraphInline.ts — duplicated here because that
 * module reaches the macros barrel through the block catalog; a test pins the
 * two equal). Raising skips groups inside a flattened body: its manifest
 * indexes the parent's event and variable tables, which raising would shift.
 */
export const INLINED_SUBGRAPH_KEY = 'traceSubgraph';

const EVENT_OPS: readonly string[] = [GraphOps.EVENT_SEND, GraphOps.EVENT_RECEIVE];
const VARIABLE_OPS: readonly string[] = [GraphOps.VARIABLE_GET, GraphOps.VARIABLE_SET];

/** Configuration keys that index a graph table, by the ops that carry them. */
function tableKeyOf(op: string | undefined): typeof MACRO_EVENT_CONFIG | typeof MACRO_VARIABLE_CONFIG | undefined {
    if (op !== undefined && EVENT_OPS.includes(op)) return MACRO_EVENT_CONFIG;
    if (op !== undefined && VARIABLE_OPS.includes(op)) return MACRO_VARIABLE_CONFIG;
    return undefined;
}

function opOf(graph: GraphData, node: GraphNode | undefined): string | undefined {
    return node ? graph.declarations[node.declaration]?.op : undefined;
}

// ============================================================================
// Grouping by tags
// ============================================================================

interface TaggedGroup {
    def: MacroDefinition;
    macroId: string;
    roles: Record<string, number>;
    duplicate: boolean;
}

function groupByTags(graph: GraphData, only?: string): TaggedGroup[] {
    // Keyed per macro so an id is only ever compared within its own macro.
    const groups = new Map<string, Map<string, TaggedGroup>>();
    graph.nodes.forEach((node, i) => {
        const meta = node.metadata;
        const macro = meta?.[TRACE_MACRO_KEY];
        if (!meta || typeof macro !== 'string' || (only !== undefined && macro !== only)) return;
        const def = getMacro(macro);
        const id = meta[MACRO_ID_KEY];
        const role = meta[MACRO_ROLE_KEY];
        if (!def || typeof id !== 'string' || typeof role !== 'string') return;
        let byId = groups.get(macro);
        if (!byId) groups.set(macro, (byId = new Map<string, TaggedGroup>()));
        let group = byId.get(id);
        if (!group) byId.set(id, (group = { def, macroId: id, roles: {}, duplicate: false }));
        if (group.roles[role] !== undefined) group.duplicate = true;
        else group.roles[role] = i;
    });
    return [...groups.values()].flatMap(byId => [...byId.values()]);
}

// ============================================================================
// Comparison against a re-emission
// ============================================================================

/** The block content a group carries: what its raised pseudo-node would hold. */
function readContent(graph: GraphData, shape: MacroShape, roles: Readonly<Record<string, number>>): MacroInstanceContent {
    const literals: Record<string, unknown> = {};
    const wired: Record<string, MacroWiredValue> = {};
    for (const [name, param] of Object.entries(shape.params)) {
        const target = param.targets[0];
        if (!target) continue;
        const node = graph.nodes[roles[target.role]];
        if (param.kind === 'config') {
            const config = node.configuration?.[target.socket];
            if (config && config.value.length > 0) literals[name] = config.value[0];
            continue;
        }
        const value = node.values?.[target.socket];
        if (!value) continue;
        if ('node' in value) wired[name] = { ...value };
        else if (value.value.length > 0) literals[name] = value.value[0];
    }
    const outputs: Record<string, FlowTarget> = {};
    for (const [name, ref] of Object.entries(shape.outputs)) {
        const flow = graph.nodes[roles[ref.role]].flows?.[ref.socket];
        if (flow) outputs[name] = { ...flow };
    }
    return { literals, wired, outputs };
}

function sameArray(a: readonly unknown[] | undefined, b: readonly unknown[] | undefined): boolean {
    const x = a ?? [];
    const y = b ?? [];
    // Object.is: a NaN literal (a disarmed period) equals itself.
    return x.length === y.length && x.every((v, i) => Object.is(v, y[i]));
}

function signatureAt(graph: GraphData, type: number): TypeSignature | undefined {
    return graph.types[type]?.signature;
}

/**
 * Compares stored members with their re-emitted counterparts. Node references
 * to members are compared through the role mapping; references to the rest of
 * the graph compare as-is (the scratch graph shares those indices). Event and
 * variable indices are matched as a one-to-one binding, then the bound entries
 * are compared.
 */
class GroupComparison {
    readonly events = new Map<number, number>();
    readonly variables = new Map<number, number>();

    constructor(
        private readonly _stored: GraphData,
        private readonly _scratch: GraphData,
        /** stored member index → scratch index */
        private readonly _members: ReadonlyMap<number, number>,
    ) {}

    node(storedIndex: number, scratchIndex: number): boolean {
        const a = this._stored.nodes[storedIndex];
        const b = this._scratch.nodes[scratchIndex];
        const op = opOf(this._stored, a);
        if (op === undefined || op !== opOf(this._scratch, b)) return false;
        return this._configuration(op, a.configuration, b.configuration) && this._values(a.values, b.values) && this._flows(a.flows, b.flows);
    }

    /** Bound events/variables hold what the shape declares. */
    tables(): boolean {
        for (const [s, x] of this.events) {
            const a = this._stored.events?.[s];
            const b = this._scratch.events?.[x];
            if (!a || !b || (a.id ?? '') !== (b.id ?? '')) return false;
            if (Object.keys(a.values ?? {}).length > 0 || Object.keys(b.values ?? {}).length > 0) return false;
        }
        for (const [s, x] of this.variables) {
            const a = this._stored.variables[s];
            const b = this._scratch.variables[x];
            // The name is derived (`macroVariableName`) and subgraph flattening
            // renames it per invocation; type and initial value are the content.
            if (!a || !b || !sameArray(a.value, b.value)) return false;
            if (signatureAt(this._stored, a.type) !== signatureAt(this._scratch, b.type)) return false;
        }
        return true;
    }

    private _ref(storedTarget: number, scratchTarget: number): boolean {
        return (this._members.get(storedTarget) ?? storedTarget) === scratchTarget;
    }

    private _bind(map: Map<number, number>, s: unknown, x: unknown): boolean {
        if (typeof s !== 'number' || typeof x !== 'number' || !Number.isInteger(s)) return false;
        const bound = map.get(s);
        if (bound !== undefined) return bound === x;
        for (const v of map.values()) if (v === x) return false;
        map.set(s, x);
        return true;
    }

    private _configuration(op: string, a: GraphNode['configuration'], b: GraphNode['configuration']): boolean {
        // An empty value array is an absent configuration (KHR drops both).
        const keys = (c: GraphNode['configuration']) => Object.keys(c ?? {}).filter(k => (c![k].value ?? []).length > 0).sort();
        const ka = keys(a);
        if (!sameArray(ka, keys(b))) return false;
        const tableKey = tableKeyOf(op);
        return ka.every(k => {
            if (k === tableKey) {
                const map = tableKey === MACRO_EVENT_CONFIG ? this.events : this.variables;
                return this._bind(map, a![k].value[0], b![k].value[0]);
            }
            return sameArray(a![k].value, b![k].value);
        });
    }

    private _values(a: GraphNode['values'], b: GraphNode['values']): boolean {
        const ka = Object.keys(a ?? {}).sort();
        if (!sameArray(ka, Object.keys(b ?? {}).sort())) return false;
        return ka.every(k => {
            const x = a![k];
            const y = b![k];
            if ('node' in x || 'node' in y) {
                return 'node' in x && 'node' in y && this._ref(x.node, y.node) && (x.socket ?? 'value') === (y.socket ?? 'value');
            }
            return signatureAt(this._stored, x.type) === signatureAt(this._scratch, y.type) && sameArray(x.value, y.value);
        });
    }

    private _flows(a: GraphNode['flows'], b: GraphNode['flows']): boolean {
        const ka = Object.keys(a ?? {}).sort();
        if (!sameArray(ka, Object.keys(b ?? {}).sort())) return false;
        return ka.every(k => this._ref(a![k].node, b![k].node) && (a![k].socket ?? 'in') === (b![k].socket ?? 'in'));
    }
}

/** `node:socket` keys of a shape's declared flow inputs or outputs, in stored indices. */
function socketKeys(refs: Readonly<Record<string, { role: string; socket: string }>>, roles: Readonly<Record<string, number>>): Set<string> {
    return new Set(Object.values(refs).map(ref => `${roles[ref.role]}:${ref.socket}`));
}

/**
 * Nothing outside the group reaches in except through declared inputs: no
 * flow into another member socket, no read of a member output, no use of its
 * private events/variables. A subgraph interface may enter at a declared input
 * and leave from a declared output — the raised block carries both — but may
 * not bind a member's data sockets.
 */
function boundaryClosed(
    graph: GraphData,
    shape: MacroShape,
    roles: Readonly<Record<string, number>>,
    members: ReadonlySet<number>,
    comparison: GroupComparison,
): boolean {
    const inputs = socketKeys(shape.inputs, roles);
    const outputs = socketKeys(shape.outputs, roles);
    for (const s of Object.values(graph.inputFlowSockets ?? {})) {
        if (members.has(s.node) && !inputs.has(`${s.node}:${s.socket}`)) return false;
    }
    for (const s of Object.values(graph.outputFlowSockets ?? {})) {
        if (members.has(s.node) && !outputs.has(`${s.node}:${s.socket}`)) return false;
    }
    const valueSockets = [...Object.values(graph.outputValueSockets ?? {}), ...Object.values(graph.inputValueSockets ?? {})];
    if (valueSockets.some(s => s.node !== undefined && members.has(s.node))) return false;
    for (let i = 0; i < graph.nodes.length; i++) {
        if (members.has(i)) continue;
        const node = graph.nodes[i];
        for (const flow of Object.values(node.flows ?? {})) {
            if (members.has(flow.node) && !inputs.has(`${flow.node}:${flow.socket ?? 'in'}`)) return false;
        }
        for (const value of Object.values(node.values ?? {})) {
            if ('node' in value && members.has(value.node)) return false;
        }
        const tableKey = tableKeyOf(opOf(graph, node));
        const index = tableKey ? node.configuration?.[tableKey]?.value?.[0] : undefined;
        if (typeof index === 'number') {
            const map = tableKey === MACRO_EVENT_CONFIG ? comparison.events : comparison.variables;
            if (map.has(index)) return false;
        }
    }
    return true;
}

interface ShapeMatch {
    instance: MacroInstance;
    shape: MacroShape;
}

function matchShape(graph: GraphData, group: TaggedGroup, shape: MacroShape, legacy: boolean): ShapeMatch | null {
    const { def, macroId, roles } = group;
    if (Object.keys(roles).length !== shape.nodes.length || shape.nodes.some(n => roles[n.role] === undefined)) return null;
    const members = new Set(Object.values(roles));
    const content = readContent(graph, shape, roles);

    // An output wired back into this same block must land on a declared input
    // (a payload re-triggering `in`); anywhere else is a rewire.
    const inputs = socketKeys(shape.inputs, roles);
    const selfTargets = Object.values(content.outputs).filter(f => members.has(f.node));
    if (selfTargets.some(f => !inputs.has(`${f.node}:${f.socket ?? 'in'}`))) return null;

    // Re-emit into a scratch copy sharing the graph's existing indices. The
    // writer only appends, and only mutates the nodes it appends.
    const scratch: GraphData = {
        ...graph,
        types: [...graph.types],
        declarations: [...graph.declarations],
        variables: [...graph.variables],
        events: [...(graph.events ?? [])],
        nodes: [...graph.nodes],
    };
    const expansion = emitMacroInstance(scratch, def, content, macroId, { shape, silent: true });
    const toScratch = new Map(shape.nodes.map(n => [roles[n.role], expansion.nodes[n.role]]));
    // Self-targeting outputs point at stored members; re-point the scratch
    // copies at their scratch counterparts so the comparison sees one block.
    for (const [name, ref] of Object.entries(shape.outputs)) {
        const flow = content.outputs[name];
        if (!flow || !members.has(flow.node)) continue;
        scratch.nodes[expansion.nodes[ref.role]].flows![ref.socket] = { node: toScratch.get(flow.node)!, socket: flow.socket };
    }

    const comparison = new GroupComparison(graph, scratch, toScratch);
    if (!shape.nodes.every(n => comparison.node(roles[n.role], expansion.nodes[n.role]))) return null;
    if (!comparison.tables()) return null;
    if (comparison.events.size !== expansion.events.length || comparison.variables.size !== expansion.variables.length) return null;
    if (!boundaryClosed(graph, shape, roles, members, comparison)) return null;

    const storedIndex = (map: ReadonlyMap<number, number>, scratchIndex: number): number =>
        [...map].find(([, x]) => x === scratchIndex)![0];
    return {
        shape,
        instance: {
            macro: def.name,
            macroId,
            nodes: roles,
            params: content.literals,
            wired: content.wired,
            outputs: content.outputs,
            events: expansion.events.map(x => storedIndex(comparison.events, x)),
            variables: expansion.variables.map(x => storedIndex(comparison.variables, x)),
            legacy,
        },
    };
}

function recognizeShapes(graph: GraphData, only?: string): ShapeMatch[] {
    const matches: ShapeMatch[] = [];
    for (const group of groupByTags(graph, only)) {
        if (group.duplicate) continue;
        const shapes: Array<[MacroShape, boolean]> = [[group.def, false], ...(group.def.legacyShapes ?? []).map((s): [MacroShape, boolean] => [s, true])];
        for (const [shape, legacy] of shapes) {
            const match = matchShape(graph, group, shape, legacy);
            if (match) {
                matches.push(match);
                break;
            }
        }
    }
    return matches;
}

/**
 * Find macro instances: tagged groups that are exactly what their raised form
 * lowers to (see the file header). Everything else is plain nodes.
 *
 * @param graph The graph to scan.
 * @param only Restrict to one macro name (defaults to every registered macro).
 */
export function recognizeMacros(graph: GraphData, only?: string): MacroInstance[] {
    return recognizeShapes(graph, only).map(m => m.instance);
}

// ============================================================================
// Raising
// ============================================================================

/** Options for {@link raiseMacros}. */
export interface RaiseMacrosOptions {
    /** Raise only this macro (by name). */
    only?: string;
    /** Raise only groups in a superseded shape (the ingestion upgrade). */
    legacyOnly?: boolean;
}

/**
 * Replace every recognized expansion with its `macro/<name>` pseudo-node —
 * the representation the editor shows and the AI assistant reads. The inverse
 * of `lowerMacros`: `lowerMacros(raiseMacros(g))` rebuilds each instance, and
 * raising a lowered raised graph gives the raised graph back.
 *
 * Each pseudo-node takes its entry node's place (so node order is stable) and
 * carries the block's params (inline, config or wired), its outbound flows by
 * output name, and the entry's author metadata plus `macroId` (so lowering
 * reuses the id). Inbound flows move to the pseudo-node, addressed by input
 * name. The instance's private events and variables are removed. Groups
 * inside a flattened subgraph body are left expanded.
 *
 * @returns A new graph (the input is not mutated), or the input itself when
 *   there is nothing to raise.
 */
export function raiseMacros(graph: GraphData, options: RaiseMacrosOptions = {}): GraphData {
    const matches = recognizeShapes(graph, options.only).filter(
        m =>
            (!options.legacyOnly || m.instance.legacy) &&
            !Object.values(m.instance.nodes).some(n => graph.nodes[n].metadata?.[INLINED_SUBGRAPH_KEY] !== undefined),
    );
    if (matches.length === 0) return graph;

    const out = structuredClone(graph);
    const removed = new Set<number>();
    const inbound = new Map<string, FlowTarget>();
    const outbound = new Map<string, FlowTarget>();
    const pseudoNodes = new Map<number, GraphNode>();
    const privateEvents = new Set<number>();
    const privateVariables = new Set<number>();

    for (const { instance, shape } of matches) {
        const def = getMacro(instance.macro)!;
        const entry = instance.nodes[shape.entryRole];
        pseudoNodes.set(entry, pseudoNode(out, def, shape, instance));
        for (const n of Object.values(instance.nodes)) if (n !== entry) removed.add(n);
        for (const [name, ref] of Object.entries(shape.inputs)) {
            const key = `${instance.nodes[ref.role]}:${ref.socket}`;
            if (!inbound.has(key)) inbound.set(key, { node: entry, socket: name });
        }
        for (const [name, ref] of Object.entries(shape.outputs)) {
            outbound.set(`${instance.nodes[ref.role]}:${ref.socket}`, { node: entry, socket: name });
        }
        for (const e of instance.events) privateEvents.add(e);
        for (const v of instance.variables) privateVariables.add(v);
    }
    for (const [index, node] of pseudoNodes) out.nodes[index] = node;

    // Inbound flows (including a pseudo-node's own outputs into another
    // block, or back into itself) address the block by input name.
    out.nodes.forEach((node, i) => {
        if (removed.has(i)) return;
        for (const [key, flow] of Object.entries(node.flows ?? {})) {
            const target = inbound.get(`${flow.node}:${flow.socket ?? 'in'}`);
            if (target) node.flows![key] = { ...target };
        }
    });
    // A subgraph interface entering or leaving a block addresses it by socket name too.
    for (const [sockets, map] of [[out.inputFlowSockets, inbound], [out.outputFlowSockets, outbound]] as const) {
        for (const socket of Object.values(sockets ?? {})) {
            const target = map.get(`${socket.node}:${socket.socket}`);
            if (target) Object.assign(socket, target);
        }
    }

    const indexMap = new Map<number, number>();
    out.nodes.forEach((_, i) => {
        if (!removed.has(i)) indexMap.set(i, indexMap.size);
    });
    out.nodes = out.nodes.filter((_, i) => !removed.has(i));
    remapNodeRefs(out, indexMap);
    removeTableEntries(out, 'events', MACRO_EVENT_CONFIG, EVENT_OPS, privateEvents);
    removeTableEntries(out, 'variables', MACRO_VARIABLE_CONFIG, VARIABLE_OPS, privateVariables);
    pruneDeclarations(out);
    return out;
}

/** The pseudo-node standing for one recognized instance (indices still pre-compaction). */
function pseudoNode(out: GraphData, def: MacroDefinition, shape: MacroShape, instance: MacroInstance): GraphNode {
    const node: GraphNode = { declaration: declarationIndex(out, def.op) };
    const values: NonNullable<GraphNode['values']> = {};
    const configuration: NonNullable<GraphNode['configuration']> = {};
    for (const [name, param] of Object.entries(shape.params)) {
        const target = param.targets[0];
        if (!target) continue;
        const source = out.nodes[instance.nodes[target.role]];
        if (param.kind === 'config') {
            const config = source.configuration?.[target.socket];
            if (config) configuration[name] = structuredClone(config);
        } else {
            const value = source.values?.[target.socket];
            if (value) values[name] = structuredClone(value);
        }
    }
    if (Object.keys(configuration).length > 0) node.configuration = configuration;
    if (Object.keys(values).length > 0) node.values = values;
    if (Object.keys(instance.outputs).length > 0) {
        node.flows = Object.fromEntries(Object.entries(instance.outputs).map(([k, f]) => [k, { ...f }]));
    }
    const { [TRACE_MACRO_KEY]: _macro, [MACRO_ROLE_KEY]: _role, ...authorMeta } = out.nodes[instance.nodes[shape.entryRole]].metadata ?? {};
    node.metadata = { ...authorMeta, [MACRO_ID_KEY]: instance.macroId };
    return node;
}

/** {@link raiseMacros} over every graph of a `BehaviorGraphData`-shaped object (same reference when unchanged). */
export function raiseBehaviorGraphData<T extends { graph?: GraphData; graphs?: GraphData[] }>(data: T): T {
    const graph = data.graph ? raiseMacros(data.graph) : data.graph;
    const graphs = data.graphs ? data.graphs.map(g => raiseMacros(g)) : data.graphs;
    const unchanged = graph === data.graph && (graphs === data.graphs || graphs!.every((g, i) => g === data.graphs![i]));
    if (unchanged) return data;
    return { ...data, ...(graph !== undefined ? { graph } : {}), ...(graphs !== undefined ? { graphs } : {}) };
}
